import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import VerificationEngine from './components/VerificationEngine';
import SocialMediaFeed from './components/SocialMediaFeed';
import PublicCameraFeed from './components/PublicCameraFeed';
import BrowserExtension from './components/BrowserExtension';
import TeamChat from './components/TeamChat';
import PredictiveAnalytics from './components/PredictiveAnalytics';
import HistoricalCases from './components/HistoricalCases';
import PublicAlertSystem from './components/PublicAlertSystem';
import IncidentReportPortal from './components/IncidentReportPortal';
import FieldResponderView from './components/FieldResponderView';
import PopulationHeatmap from './components/PopulationHeatmap';
import TrappedHeatmap from './components/TrappedHeatmap';
import CrisisMap from './components/CrisisMap';
import ImageAnalysisModal from './components/ImageAnalysisModal';
import PublicSOSButton from './components/public/PublicSOSButton';
import DisasterDashboard from './components/DisasterDashboard';
import AirQualityCard from './components/AirQualityCard';
import NewsFeed from './components/NewsFeed';
import {
  AlertTriangle,
  Flame,
  Droplets,
  Mountain,
  Building2,
  Helicopter,
  Ship,
  Truck,
  Ambulance,
  Users,
  Stethoscope,
  Pill,
  Radio,
  Camera,
  MessageCircle,
  Phone,
  Map,
  Activity,
  Play,
  RotateCw,
  Send,
  Target,
  Heart,
  Package,
  Clock,
  X,
  GitFork,
  Dog,
  Tractor,
  Wifi,
  Satellite,
  BarChart3,
  History,
  Bell,
  FileText,
  Smartphone,
  ThermometerSun,
  Users2,
} from 'lucide-react';
import './index.css';

function App() {
  const [timestamp, setTimestamp] = useState(new Date());
  const [showSOSModal, setShowSOSModal] = useState(false);
  const [selectedZone, setSelectedZone] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [activeTab, setActiveTab] = useState<'command' | 'analytics' | 'communication' | 'public'>('command');

  useEffect(() => {
    const timer = setInterval(() => setTimestamp(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const stats = [
    { label: 'ACTIVE', value: 3, color: '#ff0000', icon: AlertTriangle },
    { label: 'TEAMS', value: 6, color: '#ff6600', icon: Users },
    { label: 'SAVED', value: 124, color: '#00ffff', icon: Heart },
    { label: 'SOURCES', value: 847, color: '#ff00ff', icon: Satellite },
  ];

  const aiRecommendations = [
    { zone: 'ZONE A', action: 'IMMEDIATE EVACUATION', people: 47, confidence: 94, severity: 'critical' },
    { zone: 'ZONE B', action: 'DEPLOY 2 RESCUE TEAMS', people: 12, confidence: 87, severity: 'warning' },
    { zone: 'ZONE C', action: 'MONITOR STRUCTURE', people: 3, confidence: 76, severity: 'monitor' },
  ];

  const sosAlerts = [
    { zone: 'ZONE A', message: 'WATER RISING - LEVEL 3 CRITICAL', time: '30s', priority: 1 },
    { zone: 'ZONE A', message: 'ROOFTOP TRAPPED - 4 PEOPLE', time: '2m', priority: 1 },
    { zone: 'ZONE B', message: 'FIRE SPREADING', time: '5m', priority: 2 },
  ];

  const resources = [
    { name: 'AERIAL ASSAULT', total: 3, available: 1, icon: Helicopter, color: '#ff00ff' },
    { name: 'NAVAL SUPPORT', total: 14, available: 8, icon: Ship, color: '#00ffff' },
    { name: 'MEDICAL EVAC', total: 22, available: 10, icon: Ambulance, color: '#00ff00' },
    { name: 'TACTICAL UNITS', total: 6, available: 4, icon: Users, color: '#ff6600' },
    { name: 'K-9 UNITS', total: 4, available: 2, icon: Dog, color: '#ffaa00' },
    { name: 'HEAVY EQUIP', total: 3, available: 1, icon: Tractor, color: '#aa00ff' },
  ];

  return (
    <div className="min-h-screen bg-[#030614] text-white">
      {/* Animated Background */}
      <div className="fixed inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-[#030614] via-[#0a1a2f] to-[#030614]"></div>
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 2px 2px, cyan 1px, transparent 0)',
          backgroundSize: '40px 40px'
        }}></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 p-6 max-w-7xl mx-auto">
        {/* Header */}
        <motion.div 
          initial={{ y: -50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6 mb-6"
        >
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <GitFork className="text-cyan-500 animate-pulse" size={48} />
              <div>
                <h1 className="text-5xl font-bold text-cyan-500">VECTOR</h1>
                <p className="text-xs text-gray-400 tracking-widest">CRISIS RESPONSE PLATFORM v2.0</p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2 bg-black/50 px-4 py-2 rounded-lg border border-cyan-500/30">
                <Clock size={18} className="text-cyan-500" />
                <span className="font-mono text-xl">{timestamp.toLocaleTimeString()}</span>
              </div>
              
              <div className="flex gap-4">
                {stats.map((stat, i) => {
                  const Icon = stat.icon;
                  return (
                    <motion.div
                      key={i}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: i * 0.1 }}
                      className="text-center"
                    >
                      <div className="flex items-center gap-1 justify-center">
                        <Icon size={16} color={stat.color} />
                        <span className="text-2xl font-bold" style={{ color: stat.color }}>{stat.value}</span>
                      </div>
                      <div className="text-xs text-gray-400">{stat.label}</div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="flex gap-2 mt-4 border-t border-cyan-500/20 pt-4">
            {[
              { id: 'command', label: 'COMMAND CENTER', icon: Map },
              { id: 'analytics', label: 'ANALYTICS', icon: BarChart3 },
              { id: 'communication', label: 'COMMUNICATION', icon: Radio },
              { id: 'public', label: 'PUBLIC PORTAL', icon: Users2 },
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as 'command' | 'analytics' | 'communication' | 'public')}
                  className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm flex items-center justify-center gap-2 transition-all ${
                    activeTab === tab.id
                      ? 'bg-cyan-600 text-white shadow-lg shadow-cyan-500/50'
                      : 'bg-gray-800/50 text-gray-400 hover:bg-gray-700'
                  }`}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </motion.div>

        {/* COMMAND CENTER VIEW */}
{activeTab === 'command' && (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="space-y-6"
  >
    {/* Main Grid */}
    <div className="grid grid-cols-3 gap-6">
      {/* Map */}
      <div className="col-span-2 bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6 h-[500px]">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Map className="text-cyan-500" /> LIVE CRISIS MAP
        </h2>
        <div className="h-[400px] rounded-lg overflow-hidden">
          <CrisisMap />
        </div>
      </div>

      {/* AI Recommendations */}
      <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Activity className="text-fuchsia-500" /> AI RECOMMENDATIONS
        </h2>
        <div className="space-y-4">
          {aiRecommendations.map((rec, i) => (
            <motion.div
              key={i}
              whileHover={{ scale: 1.02 }}
              className={`border-l-4 pl-3 p-2 ${
                rec.severity === 'critical' ? 'border-red-500 bg-red-500/5' :
                rec.severity === 'warning' ? 'border-orange-500 bg-orange-500/5' : 'border-cyan-500 bg-cyan-500/5'
              }`}
            >
              <div className="flex justify-between">
                <span className="font-bold">{rec.zone}</span>
                <span className={`text-sm ${
                  rec.confidence > 80 ? 'text-green-500' : 'text-yellow-500'
                }`}>{rec.confidence}%</span>
              </div>
              <p className="text-sm text-gray-400">{rec.action}</p>
              <p className="text-xs text-gray-500 mt-1">{rec.people} people affected</p>
              <button className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white text-sm font-bold py-2 px-4 rounded-lg mt-2">
                DEPLOY RESOURCES
              </button>
            </motion.div>
          ))}
          
          <button
            onClick={() => setShowImageModal(true)}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-3 px-4 rounded-lg flex items-center justify-center gap-2 mt-4"
          >
            <Camera size={16} />
            AI IMAGE ANALYSIS
          </button>
        </div>
      </div>

      {/* SOS Feed */}
      <div className="col-span-2 bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6 h-[400px]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Radio className="text-red-500 animate-pulse" /> SOS ALERTS
          </h2>
          <button
            onClick={() => setShowSOSModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white text-sm font-bold py-2 px-4 rounded-lg"
          >
            NEW ALERT
          </button>
        </div>
        <div className="space-y-3 h-[300px] overflow-y-auto custom-scroll">
          {sosAlerts.map((sos, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 }}
              className="bg-black/50 p-4 rounded-lg border border-gray-700 hover:border-red-500/50 transition-all"
            >
              <div className="flex justify-between">
                <span className="font-bold text-red-500">{sos.zone}</span>
                <span className="text-xs text-gray-400">{sos.time}</span>
              </div>
              <p className="text-sm mt-1">"{sos.message}"</p>
              <div className="flex justify-between mt-2">
                <span className="text-xs bg-red-500/20 text-red-500 px-2 py-1 rounded">
                  PRIORITY {sos.priority}
                </span>
                <button className="text-xs text-cyan-500 hover:text-cyan-400 flex items-center gap-1">
                  <Target size={12} /> ASSIGN →
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Resources */}
      <div className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6">
        <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
          <Package className="text-yellow-500" /> RESOURCES
        </h2>
        <div className="space-y-4 max-h-[300px] overflow-y-auto custom-scroll">
          {resources.map((res, i) => {
            const percentage = (res.available / res.total) * 100;
            const Icon = res.icon;
            return (
              <motion.div
                key={i}
                initial={{ width: 0 }}
                animate={{ width: '100%' }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="flex justify-between text-sm mb-1">
                  <div className="flex items-center gap-2">
                    <Icon size={16} color={res.color} />
                    <span>{res.name}</span>
                  </div>
                  <span style={{ color: res.color }}>{res.available}/{res.total}</span>
                </div>
                <div className="w-full bg-gray-700 h-2 rounded-full overflow-hidden">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${percentage}%` }}
                    transition={{ duration: 1, delay: i * 0.1 }}
                    className="h-2 rounded-full"
                    style={{ 
                      backgroundColor: res.color,
                      boxShadow: `0 0 10px ${res.color}`
                    }}
                  />
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Gap Analysis */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-6 p-4 bg-red-500/10 rounded-lg border border-red-500/30"
        >
          <p className="text-sm font-bold text-red-500 mb-2 flex items-center gap-2">
            <AlertTriangle size={16} />
            CRITICAL GAPS
          </p>
          <div className="text-sm space-y-1">
            <div className="flex justify-between">
              <span>ZONE A:</span>
              <span className="text-red-500">+6 boats, +3 teams</span>
            </div>
            <div className="flex justify-between">
              <span>ZONE B:</span>
              <span className="text-orange-500">+1 team</span>
            </div>
          </div>
          <button className="w-full bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white text-sm font-bold py-2 px-4 rounded-lg mt-3 flex items-center justify-center gap-2">
            <RotateCw size={14} />
            REALLOCATE
          </button>
        </motion.div>
      </div>
    </div>

    {/* Criticality Clock */}
    <motion.div
      initial={{ y: 50, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      className="bg-gray-900/50 backdrop-blur-xl border border-cyan-500/30 rounded-xl p-6"
    >
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Target className="text-red-500 animate-pulse" size={40} />
          <div>
            <span className="text-lg font-bold">ZONE A CRITICALITY</span>
            <span className="ml-4 text-red-500 text-4xl font-mono font-bold">25:00</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Heart className="text-red-500" size={20} />
            <span className="text-sm">47 LIVES AT RISK</span>
          </div>
          <button className="bg-gradient-to-r from-red-600 to-orange-600 hover:from-red-700 hover:to-orange-700 text-white font-bold py-3 px-6 rounded-lg">
            EVACUATE NOW
          </button>
        </div>
      </div>
      <div className="w-full bg-gray-700 h-4 rounded-full mt-4 overflow-hidden">
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: '25%' }}
          transition={{ duration: 1 }}
          className="h-4 rounded-full bg-gradient-to-r from-red-600 to-orange-500"
          style={{ boxShadow: '0 0 20px #ff0000' }}
        />
      </div>
    </motion.div>

    {/* Verification Engine */}
    <div className="mt-6">
      <VerificationEngine />
    </div>

    {/* Social Media Feed - ADD THIS SECTION */}
    <div className="mt-6">
      <SocialMediaFeed />
    </div>
  </motion.div>
)}

        {/* ANALYTICS VIEW */}
{activeTab === 'analytics' && (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="space-y-6"
  >
    {/* New cards grid at the very top */}
    <div className="grid grid-cols-2 gap-4">
      <AirQualityCard />
      <NewsFeed />
    </div>
    
    {/* Disaster Dashboard */}
    <DisasterDashboard />
    
    {/* Existing analytics components */}
    <div className="grid grid-cols-2 gap-6">
      <PredictiveAnalytics />
      <HistoricalCases />
    </div>
    <div className="grid grid-cols-2 gap-6">
      <PopulationHeatmap />
      <TrappedHeatmap />
    </div>
  </motion.div>
)}

        {/* COMMUNICATION VIEW */}
        {activeTab === 'communication' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-2 gap-6"
          >
            <TeamChat />
            <div className="space-y-6">
              <BrowserExtension />
              <PublicCameraFeed />
            </div>
          </motion.div>
        )}

        {/* PUBLIC VIEW */}
        {activeTab === 'public' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="grid grid-cols-2 gap-6"
          >
            <IncidentReportPortal />
            <div className="space-y-6">
              <PublicAlertSystem />
              <FieldResponderView />
            </div>
          </motion.div>
        )}

        {/* Social Media Feed - Always visible at bottom */}
        <div className="mt-6">
          <SocialMediaFeed />
        </div>

        {/* Modals */}
        <ImageAnalysisModal
          isOpen={showImageModal}
          onClose={() => setShowImageModal(false)}
        />

        <PublicSOSButton />
      </div>
    </div>
  );
}

export default App;